<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  selectedTimezone: {
    type: String,
    default: 'UTC'
  }
})

const emit = defineEmits(['update:selectedTimezone'])

const searchQuery = ref('')
const showDropdown = ref(false)
const useDaylightSavings = ref(false)

// Comprehensive timezone data with all world countries
const timezones = ref([
  // UTC
  { name: 'UTC', timezone: 'UTC', offset: '+00:00', countries: ['Coordinated Universal Time'] },
  
  // North America
  { name: 'Alaska Time', timezone: 'America/Anchorage', offset: '-09:00', countries: ['Alaska'] },
  { name: 'Atlantic Time', timezone: 'America/Halifax', offset: '-04:00', countries: ['Canada Atlantic', 'Dominican Republic', 'Puerto Rico'] },
  { name: 'Central Time', timezone: 'America/Chicago', offset: '-06:00', countries: ['US Central', 'Canada Central', 'Mexico Central', 'Guatemala', 'Honduras', 'El Salvador', 'Nicaragua', 'Costa Rica', 'Belize'] },
  { name: 'Eastern Time', timezone: 'America/New_York', offset: '-05:00', countries: ['US East Coast', 'Canada Eastern', 'Bahamas', 'Cayman Islands', 'Jamaica', 'Haiti', 'Cuba'] },
  { name: 'Hawaii Time', timezone: 'Pacific/Honolulu', offset: '-10:00', countries: ['Hawaii'] },
  { name: 'Mountain Time', timezone: 'America/Denver', offset: '-07:00', countries: ['US Mountain', 'Canada Mountain', 'Mexico Mountain'] },
  { name: 'Newfoundland Time', timezone: 'America/St_Johns', offset: '-03:30', countries: ['Canada Newfoundland'] },
  { name: 'Pacific Time', timezone: 'America/Los_Angeles', offset: '-08:00', countries: ['US West Coast', 'Canada Western', 'Mexico Pacific'] },
  
  // South America
  { name: 'Bolivia Time', timezone: 'America/La_Paz', offset: '-04:00', countries: ['Bolivia'] },
  { name: 'Brasília Time', timezone: 'America/Sao_Paulo', offset: '-03:00', countries: ['Brazil', 'Argentina', 'Uruguay', 'Paraguay', 'Chile', 'Suriname', 'French Guiana'] },
  { name: 'Colombia Time', timezone: 'America/Bogota', offset: '-05:00', countries: ['Colombia', 'Peru', 'Ecuador', 'Panama'] },
  { name: 'Falkland Islands Time', timezone: 'Atlantic/Stanley', offset: '-03:00', countries: ['Falkland Islands'] },
  { name: 'Guyana Time', timezone: 'America/Guyana', offset: '-04:00', countries: ['Guyana'] },
  { name: 'Venezuela Time', timezone: 'America/Caracas', offset: '-04:00', countries: ['Venezuela'] },
  
  // Europe
  { name: 'Estonia Time', timezone: 'Europe/Tallinn', offset: '+02:00', countries: ['Estonia'] },
  { name: 'Latvia Time', timezone: 'Europe/Riga', offset: '+02:00', countries: ['Latvia'] },
  { name: 'Lithuania Time', timezone: 'Europe/Vilnius', offset: '+02:00', countries: ['Lithuania'] },
  { name: 'Central Europe', timezone: 'Europe/Paris', offset: '+01:00', countries: ['France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Luxembourg', 'Switzerland', 'Austria', 'Slovenia', 'Croatia', 'Bosnia', 'Serbia', 'Montenegro', 'North Macedonia', 'Albania', 'Poland', 'Czech Republic', 'Slovakia', 'Hungary', 'Denmark', 'Sweden', 'Norway', 'Finland'] },
  { name: 'Eastern Europe', timezone: 'Europe/Bucharest', offset: '+02:00', countries: ['Romania', 'Bulgaria', 'Greece', 'Cyprus', 'Ukraine', 'Belarus', 'Moldova', 'Turkey', 'Israel', 'Lebanon', 'Syria', 'Jordan', 'Palestine', 'Egypt', 'Libya', 'South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Malawi', 'Zambia', 'Mozambique', 'Lesotho', 'Eswatini'] },
  { name: 'Irkutsk Time', timezone: 'Asia/Irkutsk', offset: '+08:00', countries: ['Russia Irkutsk'] },
  { name: 'Kamchatka Time', timezone: 'Asia/Kamchatka', offset: '+12:00', countries: ['Russia Kamchatka'] },
  { name: 'Krasnoyarsk Time', timezone: 'Asia/Krasnoyarsk', offset: '+07:00', countries: ['Russia Krasnoyarsk', 'Mongolia'] },
  { name: 'London Time', timezone: 'Europe/London', offset: '+00:00', countries: ['UK', 'Ireland', 'Portugal', 'Iceland', 'Faroe Islands'] },
  { name: 'Magadan Time', timezone: 'Asia/Magadan', offset: '+11:00', countries: ['Russia Magadan'] },
  { name: 'Moscow Time', timezone: 'Europe/Moscow', offset: '+03:00', countries: ['Russia Western', 'Belarus', 'Ukraine', 'Georgia', 'Armenia', 'Azerbaijan', 'Kazakhstan', 'Uzbekistan', 'Turkmenistan', 'Tajikistan', 'Kyrgyzstan', 'Iraq', 'Saudi Arabia', 'Kuwait', 'Bahrain', 'Qatar', 'Oman', 'Yemen', 'Somalia', 'Djibouti', 'Eritrea', 'Ethiopia', 'Kenya', 'Tanzania', 'Uganda', 'Rwanda', 'Burundi', 'Madagascar', 'Comoros', 'Mayotte', 'Réunion', 'Seychelles', 'Mauritius'] },
  { name: 'Omsk Time', timezone: 'Asia/Omsk', offset: '+06:00', countries: ['Russia Omsk', 'Kazakhstan', 'Kyrgyzstan'] },
  { name: 'Samara Time', timezone: 'Europe/Samara', offset: '+04:00', countries: ['Russia Samara', 'Azerbaijan', 'Georgia', 'Armenia'] },
  { name: 'Vladivostok Time', timezone: 'Asia/Vladivostok', offset: '+10:00', countries: ['Russia Vladivostok'] },
  { name: 'Yakutsk Time', timezone: 'Asia/Yakutsk', offset: '+09:00', countries: ['Russia Yakutsk'] },
  { name: 'Yekaterinburg Time', timezone: 'Asia/Yekaterinburg', offset: '+05:00', countries: ['Russia Yekaterinburg', 'Pakistan', 'Uzbekistan', 'Turkmenistan', 'Tajikistan', 'Kyrgyzstan', 'Kazakhstan'] },
  
  // Asia
  { name: 'Almaty Time', timezone: 'Asia/Almaty', offset: '+06:00', countries: ['Kazakhstan'] },
  { name: 'Ashgabat Time', timezone: 'Asia/Ashgabat', offset: '+05:00', countries: ['Turkmenistan'] },
  { name: 'Astana Time', timezone: 'Asia/Astana', offset: '+06:00', countries: ['Kazakhstan'] },
  { name: 'Bandar Seri Begawan Time', timezone: 'Asia/Brunei', offset: '+08:00', countries: ['Brunei'] },
  { name: 'Bangkok Time', timezone: 'Asia/Bangkok', offset: '+07:00', countries: ['Thailand'] },
  { name: 'Beijing Time', timezone: 'Asia/Shanghai', offset: '+08:00', countries: ['China'] },
  { name: 'Bishkek Time', timezone: 'Asia/Bishkek', offset: '+06:00', countries: ['Kyrgyzstan'] },
  { name: 'Colombo Time', timezone: 'Asia/Colombo', offset: '+05:30', countries: ['Sri Lanka'] },
  { name: 'Dhaka Time', timezone: 'Asia/Dhaka', offset: '+06:00', countries: ['Bangladesh'] },
  { name: 'Dubai Time', timezone: 'Asia/Dubai', offset: '+04:00', countries: ['UAE', 'Saudi Arabia', 'Kuwait', 'Bahrain', 'Qatar', 'Oman', 'Yemen'] },
  { name: 'Dushanbe Time', timezone: 'Asia/Dushanbe', offset: '+05:00', countries: ['Tajikistan'] },
  { name: 'Ho Chi Minh Time', timezone: 'Asia/Ho_Chi_Minh', offset: '+07:00', countries: ['Vietnam'] },
  { name: 'Hong Kong Time', timezone: 'Asia/Hong_Kong', offset: '+08:00', countries: ['Hong Kong'] },
  { name: 'Islamabad Time', timezone: 'Asia/Karachi', offset: '+05:00', countries: ['Pakistan'] },
  { name: 'Jakarta Time', timezone: 'Asia/Jakarta', offset: '+07:00', countries: ['Indonesia Western'] },
  { name: 'Jayapura Time', timezone: 'Asia/Jayapura', offset: '+09:00', countries: ['Indonesia Eastern'] },
  { name: 'Kabul Time', timezone: 'Asia/Kabul', offset: '+04:30', countries: ['Afghanistan'] },
  { name: 'Karachi Time', timezone: 'Asia/Karachi', offset: '+05:00', countries: ['Pakistan'] },
  { name: 'Kathmandu Time', timezone: 'Asia/Kathmandu', offset: '+05:45', countries: ['Nepal'] },
  { name: 'Kuala Lumpur Time', timezone: 'Asia/Kuala_Lumpur', offset: '+08:00', countries: ['Malaysia'] },
  { name: 'Macau Time', timezone: 'Asia/Macau', offset: '+08:00', countries: ['Macau'] },
  { name: 'Makassar Time', timezone: 'Asia/Makassar', offset: '+08:00', countries: ['Indonesia Central'] },
  { name: 'Manila Time', timezone: 'Asia/Manila', offset: '+08:00', countries: ['Philippines'] },
  { name: 'Mumbai Time', timezone: 'Asia/Kolkata', offset: '+05:30', countries: ['India', 'Sri Lanka'] },
  { name: 'New Delhi Time', timezone: 'Asia/Kolkata', offset: '+05:30', countries: ['India'] },
  { name: 'Phnom Penh Time', timezone: 'Asia/Phnom_Penh', offset: '+07:00', countries: ['Cambodia'] },
  { name: 'Pyongyang Time', timezone: 'Asia/Pyongyang', offset: '+09:00', countries: ['North Korea'] },
  { name: 'Seoul Time', timezone: 'Asia/Seoul', offset: '+09:00', countries: ['South Korea'] },
  { name: 'Shanghai Time', timezone: 'Asia/Shanghai', offset: '+08:00', countries: ['China', 'Hong Kong', 'Macau', 'Taiwan', 'Singapore', 'Malaysia', 'Philippines', 'Brunei'] },
  { name: 'Singapore Time', timezone: 'Asia/Singapore', offset: '+08:00', countries: ['Singapore'] },
  { name: 'Taipei Time', timezone: 'Asia/Taipei', offset: '+08:00', countries: ['Taiwan'] },
  { name: 'Tashkent Time', timezone: 'Asia/Tashkent', offset: '+05:00', countries: ['Uzbekistan'] },
  { name: 'Tehran Time', timezone: 'Asia/Tehran', offset: '+03:30', countries: ['Iran'] },
  { name: 'Thimphu Time', timezone: 'Asia/Thimphu', offset: '+06:00', countries: ['Bhutan'] },
  { name: 'Tokyo Time', timezone: 'Asia/Tokyo', offset: '+09:00', countries: ['Japan', 'South Korea', 'North Korea'] },
  { name: 'Ulaanbaatar Time', timezone: 'Asia/Ulaanbaatar', offset: '+08:00', countries: ['Mongolia'] },
  { name: 'Vientiane Time', timezone: 'Asia/Vientiane', offset: '+07:00', countries: ['Laos'] },
  { name: 'Yangon Time', timezone: 'Asia/Yangon', offset: '+06:30', countries: ['Myanmar'] },
  
  // Australia & Pacific
  { name: 'Adelaide Time', timezone: 'Australia/Adelaide', offset: '+09:30', countries: ['Australia Central'] },
  { name: 'Auckland Time', timezone: 'Pacific/Auckland', offset: '+12:00', countries: ['New Zealand'] },
  { name: 'Darwin Time', timezone: 'Australia/Darwin', offset: '+09:30', countries: ['Australia Northern'] },
  { name: 'Fiji Time', timezone: 'Pacific/Fiji', offset: '+12:00', countries: ['Fiji'] },
  { name: 'Kiribati Time', timezone: 'Pacific/Tarawa', offset: '+12:00', countries: ['Kiribati'] },
  { name: 'Marshall Islands Time', timezone: 'Pacific/Majuro', offset: '+12:00', countries: ['Marshall Islands'] },
  { name: 'Micronesia Time', timezone: 'Pacific/Pohnpei', offset: '+11:00', countries: ['Micronesia'] },
  { name: 'Nauru Time', timezone: 'Pacific/Nauru', offset: '+12:00', countries: ['Nauru'] },
  { name: 'New Caledonia Time', timezone: 'Pacific/Noumea', offset: '+11:00', countries: ['New Caledonia'] },
  { name: 'Palau Time', timezone: 'Pacific/Palau', offset: '+09:00', countries: ['Palau'] },
  { name: 'Papua New Guinea Time', timezone: 'Pacific/Port_Moresby', offset: '+10:00', countries: ['Papua New Guinea'] },
  { name: 'Perth Time', timezone: 'Australia/Perth', offset: '+08:00', countries: ['Australia Western'] },
  { name: 'Samoa Time', timezone: 'Pacific/Apia', offset: '+13:00', countries: ['Samoa'] },
  { name: 'Solomon Islands Time', timezone: 'Pacific/Guadalcanal', offset: '+11:00', countries: ['Solomon Islands'] },
  { name: 'Sydney Time', timezone: 'Australia/Sydney', offset: '+10:00', countries: ['Australia Eastern', 'New Zealand'] },
  { name: 'Tonga Time', timezone: 'Pacific/Tongatapu', offset: '+13:00', countries: ['Tonga'] },
  { name: 'Tuvalu Time', timezone: 'Pacific/Funafuti', offset: '+12:00', countries: ['Tuvalu'] },
  { name: 'Vanuatu Time', timezone: 'Pacific/Efate', offset: '+11:00', countries: ['Vanuatu'] },
  
  // Africa
  { name: 'Abidjan Time', timezone: 'Africa/Abidjan', offset: '+00:00', countries: ['Côte d\'Ivoire'] },
  { name: 'Accra Time', timezone: 'Africa/Accra', offset: '+00:00', countries: ['Ghana'] },
  { name: 'Addis Ababa Time', timezone: 'Africa/Addis_Ababa', offset: '+03:00', countries: ['Ethiopia'] },
  { name: 'Algiers Time', timezone: 'Africa/Algiers', offset: '+01:00', countries: ['Algeria', 'Tunisia'] },
  { name: 'Asmara Time', timezone: 'Africa/Asmara', offset: '+03:00', countries: ['Eritrea'] },
  { name: 'Bamako Time', timezone: 'Africa/Bamako', offset: '+00:00', countries: ['Mali'] },
  { name: 'Bangui Time', timezone: 'Africa/Bangui', offset: '+01:00', countries: ['Central African Republic'] },
  { name: 'Banjul Time', timezone: 'Africa/Banjul', offset: '+00:00', countries: ['Gambia'] },
  { name: 'Bissau Time', timezone: 'Africa/Bissau', offset: '+00:00', countries: ['Guinea-Bissau'] },
  { name: 'Brazzaville Time', timezone: 'Africa/Brazzaville', offset: '+01:00', countries: ['Congo'] },
  { name: 'Bujumbura Time', timezone: 'Africa/Bujumbura', offset: '+02:00', countries: ['Burundi'] },
  { name: 'Cairo Time', timezone: 'Africa/Cairo', offset: '+02:00', countries: ['Egypt', 'Libya'] },
  { name: 'Casablanca Time', timezone: 'Africa/Casablanca', offset: '+00:00', countries: ['Morocco', 'Western Sahara'] },
  { name: 'Conakry Time', timezone: 'Africa/Conakry', offset: '+00:00', countries: ['Guinea'] },
  { name: 'Dakar Time', timezone: 'Africa/Dakar', offset: '+00:00', countries: ['Senegal'] },
  { name: 'Dar es Salaam Time', timezone: 'Africa/Dar_es_Salaam', offset: '+03:00', countries: ['Tanzania'] },
  { name: 'Djibouti Time', timezone: 'Africa/Djibouti', offset: '+03:00', countries: ['Djibouti'] },
  { name: 'Freetown Time', timezone: 'Africa/Freetown', offset: '+00:00', countries: ['Sierra Leone'] },
  { name: 'Gaborone Time', timezone: 'Africa/Gaborone', offset: '+02:00', countries: ['Botswana'] },
  { name: 'Harare Time', timezone: 'Africa/Harare', offset: '+02:00', countries: ['Zimbabwe'] },
  { name: 'Johannesburg Time', timezone: 'Africa/Johannesburg', offset: '+02:00', countries: ['South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Malawi', 'Zambia', 'Mozambique', 'Lesotho', 'Eswatini'] },
  { name: 'Juba Time', timezone: 'Africa/Juba', offset: '+03:00', countries: ['South Sudan'] },
  { name: 'Kampala Time', timezone: 'Africa/Kampala', offset: '+03:00', countries: ['Uganda'] },
  { name: 'Khartoum Time', timezone: 'Africa/Khartoum', offset: '+02:00', countries: ['Sudan'] },
  { name: 'Kigali Time', timezone: 'Africa/Kigali', offset: '+02:00', countries: ['Rwanda'] },
  { name: 'Kinshasa Time', timezone: 'Africa/Kinshasa', offset: '+01:00', countries: ['DR Congo Western'] },
  { name: 'Lagos Time', timezone: 'Africa/Lagos', offset: '+01:00', countries: ['Nigeria', 'Ghana', 'Senegal', 'Gambia', 'Guinea-Bissau', 'Guinea', 'Sierra Leone', 'Liberia', 'Côte d\'Ivoire', 'Burkina Faso', 'Mali', 'Niger', 'Chad', 'Central African Republic', 'Cameroon', 'Equatorial Guinea', 'Gabon', 'Congo', 'DR Congo Western', 'Angola'] },
  { name: 'Libreville Time', timezone: 'Africa/Libreville', offset: '+01:00', countries: ['Gabon'] },
  { name: 'Lilongwe Time', timezone: 'Africa/Lilongwe', offset: '+02:00', countries: ['Malawi'] },
  { name: 'Lomé Time', timezone: 'Africa/Lome', offset: '+00:00', countries: ['Togo'] },
  { name: 'Luanda Time', timezone: 'Africa/Luanda', offset: '+01:00', countries: ['Angola'] },
  { name: 'Lubumbashi Time', timezone: 'Africa/Lubumbashi', offset: '+02:00', countries: ['DR Congo Eastern'] },
  { name: 'Lusaka Time', timezone: 'Africa/Lusaka', offset: '+02:00', countries: ['Zambia'] },
  { name: 'Malabo Time', timezone: 'Africa/Malabo', offset: '+01:00', countries: ['Equatorial Guinea'] },
  { name: 'Maputo Time', timezone: 'Africa/Maputo', offset: '+02:00', countries: ['Mozambique'] },
  { name: 'Maseru Time', timezone: 'Africa/Maseru', offset: '+02:00', countries: ['Lesotho'] },
  { name: 'Mbabane Time', timezone: 'Africa/Mbabane', offset: '+02:00', countries: ['Eswatini'] },
  { name: 'Mogadishu Time', timezone: 'Africa/Mogadishu', offset: '+03:00', countries: ['Somalia'] },
  { name: 'Monrovia Time', timezone: 'Africa/Monrovia', offset: '+00:00', countries: ['Liberia'] },
  { name: 'N\'Djamena Time', timezone: 'Africa/Ndjamena', offset: '+01:00', countries: ['Chad'] },
  { name: 'Nairobi Time', timezone: 'Africa/Nairobi', offset: '+03:00', countries: ['Kenya', 'Tanzania', 'Uganda', 'Rwanda', 'Burundi', 'Somalia', 'Djibouti', 'Eritrea', 'Ethiopia', 'South Sudan', 'Sudan'] },
  { name: 'Niamey Time', timezone: 'Africa/Niamey', offset: '+01:00', countries: ['Niger'] },
  { name: 'Ouagadougou Time', timezone: 'Africa/Ouagadougou', offset: '+00:00', countries: ['Burkina Faso'] },
  { name: 'Porto-Novo Time', timezone: 'Africa/Porto-Novo', offset: '+01:00', countries: ['Benin'] },
  { name: 'Tripoli Time', timezone: 'Africa/Tripoli', offset: '+02:00', countries: ['Libya'] },
  { name: 'Windhoek Time', timezone: 'Africa/Windhoek', offset: '+02:00', countries: ['Namibia'] },
  { name: 'Yaoundé Time', timezone: 'Africa/Douala', offset: '+01:00', countries: ['Cameroon'] },
  
  // Middle East
  { name: 'Amman Time', timezone: 'Asia/Amman', offset: '+03:00', countries: ['Jordan'] },
  { name: 'Baghdad Time', timezone: 'Asia/Baghdad', offset: '+03:00', countries: ['Iraq'] },
  { name: 'Bahrain Time', timezone: 'Asia/Bahrain', offset: '+03:00', countries: ['Bahrain'] },
  { name: 'Beirut Time', timezone: 'Asia/Beirut', offset: '+03:00', countries: ['Lebanon'] },
  { name: 'Damascus Time', timezone: 'Asia/Damascus', offset: '+03:00', countries: ['Syria'] },
  { name: 'Jerusalem Time', timezone: 'Asia/Jerusalem', offset: '+03:00', countries: ['Israel', 'Palestine'] },
  { name: 'Kuwait Time', timezone: 'Asia/Kuwait', offset: '+03:00', countries: ['Kuwait'] },
  { name: 'Muscat Time', timezone: 'Asia/Muscat', offset: '+04:00', countries: ['Oman'] },
  { name: 'Qatar Time', timezone: 'Asia/Qatar', offset: '+03:00', countries: ['Qatar'] },
  { name: 'Riyadh Time', timezone: 'Asia/Riyadh', offset: '+03:00', countries: ['Saudi Arabia'] },
  { name: 'Sana\'a Time', timezone: 'Asia/Aden', offset: '+03:00', countries: ['Yemen'] },
  
  // Pacific Islands
  { name: 'Apia Time', timezone: 'Pacific/Apia', offset: '+13:00', countries: ['Samoa'] },
  { name: 'Funafuti Time', timezone: 'Pacific/Funafuti', offset: '+12:00', countries: ['Tuvalu'] },
  { name: 'Honiara Time', timezone: 'Pacific/Guadalcanal', offset: '+11:00', countries: ['Solomon Islands'] },
  { name: 'Koror Time', timezone: 'Pacific/Palau', offset: '+09:00', countries: ['Palau'] },
  { name: 'Majuro Time', timezone: 'Pacific/Majuro', offset: '+12:00', countries: ['Marshall Islands'] },
  { name: 'Nouméa Time', timezone: 'Pacific/Noumea', offset: '+11:00', countries: ['New Caledonia'] },
  { name: 'Nuku\'alofa Time', timezone: 'Pacific/Tongatapu', offset: '+13:00', countries: ['Tonga'] },
  { name: 'Pago Pago Time', timezone: 'Pacific/Pago_Pago', offset: '-11:00', countries: ['American Samoa'] },
  { name: 'Pohnpei Time', timezone: 'Pacific/Pohnpei', offset: '+11:00', countries: ['Micronesia'] },
  { name: 'Port Moresby Time', timezone: 'Pacific/Port_Moresby', offset: '+10:00', countries: ['Papua New Guinea'] },
  { name: 'Port Vila Time', timezone: 'Pacific/Efate', offset: '+11:00', countries: ['Vanuatu'] },
  { name: 'Suva Time', timezone: 'Pacific/Fiji', offset: '+12:00', countries: ['Fiji'] },
  { name: 'Tarawa Time', timezone: 'Pacific/Tarawa', offset: '+12:00', countries: ['Kiribati'] },
  { name: 'Yaren Time', timezone: 'Pacific/Nauru', offset: '+12:00', countries: ['Nauru'] },
  
  // Atlantic Islands
  { name: 'Azores Time', timezone: 'Atlantic/Azores', offset: '-01:00', countries: ['Azores'] },
  { name: 'Bermuda Time', timezone: 'Atlantic/Bermuda', offset: '-04:00', countries: ['Bermuda'] },
  { name: 'Canary Islands Time', timezone: 'Atlantic/Canary', offset: '+00:00', countries: ['Canary Islands'] },
  { name: 'Cape Verde Time', timezone: 'Atlantic/Cape_Verde', offset: '-01:00', countries: ['Cape Verde'] },
  { name: 'Faroe Islands Time', timezone: 'Atlantic/Faroe', offset: '+00:00', countries: ['Faroe Islands'] },
  { name: 'Madeira Time', timezone: 'Atlantic/Madeira', offset: '+00:00', countries: ['Madeira'] },
  { name: 'Reykjavik Time', timezone: 'Atlantic/Reykjavik', offset: '+00:00', countries: ['Iceland'] },
  { name: 'South Georgia Time', timezone: 'Atlantic/South_Georgia', offset: '-02:00', countries: ['South Georgia'] },
  
  // Indian Ocean
  { name: 'Chagos Time', timezone: 'Indian/Chagos', offset: '+06:00', countries: ['British Indian Ocean Territory'] },
  { name: 'Christmas Island Time', timezone: 'Indian/Christmas', offset: '+07:00', countries: ['Christmas Island'] },
  { name: 'Cocos Islands Time', timezone: 'Indian/Cocos', offset: '+06:30', countries: ['Cocos Islands'] },
  { name: 'Comoros Time', timezone: 'Indian/Comoro', offset: '+03:00', countries: ['Comoros'] },
  { name: 'Maldives Time', timezone: 'Indian/Maldives', offset: '+05:00', countries: ['Maldives'] },
  { name: 'Mauritius Time', timezone: 'Indian/Mauritius', offset: '+04:00', countries: ['Mauritius'] },
  { name: 'Mayotte Time', timezone: 'Indian/Mayotte', offset: '+03:00', countries: ['Mayotte'] },
  { name: 'Réunion Time', timezone: 'Indian/Reunion', offset: '+04:00', countries: ['Réunion'] },
  { name: 'Seychelles Time', timezone: 'Indian/Seychelles', offset: '+04:00', countries: ['Seychelles'] },
  
  // Arctic
  { name: 'Longyearbyen Time', timezone: 'Arctic/Longyearbyen', offset: '+01:00', countries: ['Svalbard'] },
  
  // Antarctica
  { name: 'Casey Time', timezone: 'Antarctica/Casey', offset: '+08:00', countries: ['Antarctica Casey'] },
  { name: 'Davis Time', timezone: 'Antarctica/Davis', offset: '+07:00', countries: ['Antarctica Davis'] },
  { name: 'Dumont d\'Urville Time', timezone: 'Antarctica/DumontDUrville', offset: '+10:00', countries: ['Antarctica Dumont d\'Urville'] },
  { name: 'Mawson Time', timezone: 'Antarctica/Mawson', offset: '+05:00', countries: ['Antarctica Mawson'] },
  { name: 'McMurdo Time', timezone: 'Antarctica/McMurdo', offset: '+12:00', countries: ['Antarctica'] },
  { name: 'Palmer Time', timezone: 'Antarctica/Palmer', offset: '-03:00', countries: ['Antarctica Palmer'] },
  { name: 'Rothera Time', timezone: 'Antarctica/Rothera', offset: '-03:00', countries: ['Antarctica Rothera'] },
  { name: 'Syowa Time', timezone: 'Antarctica/Syowa', offset: '+03:00', countries: ['Antarctica Syowa'] },
  { name: 'Vostok Time', timezone: 'Antarctica/Vostok', offset: '+06:00', countries: ['Antarctica Vostok'] }
])

// Extract city/capital name from IANA timezone identifier
const getCityFromTimezone = (timezone) => {
  // Examples: "Europe/Tallinn" -> "Tallinn", "America/New_York" -> "New York"
  const parts = timezone.split('/')
  if (parts.length > 1) {
    return parts[parts.length - 1].replace(/_/g, ' ')
  }
  return ''
}

const filteredTimezones = computed(() => {
  let filtered = timezones.value
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase().trim()
    
    filtered = timezones.value.filter(tz => {
      // Search in timezone name (e.g., "Tokyo Time", "Central Europe")
      if (tz.name.toLowerCase().includes(query)) return true
      
      // Search in IANA timezone identifier (e.g., "Europe/Tallinn")
      if (tz.timezone.toLowerCase().includes(query)) return true
      
      // Extract and search in city/capital name from timezone
      const cityName = getCityFromTimezone(tz.timezone).toLowerCase()
      if (cityName.includes(query)) return true
      
      // Search in countries array
      const countryMatch = tz.countries.some(country => {
        const countryLower = country.toLowerCase()
        // Check if query matches country name (partial or full)
        return countryLower.includes(query) || query.includes(countryLower)
      })
      if (countryMatch) return true
      
      // Also check if any country name starts with the query (for better matching)
      const startsWithMatch = tz.countries.some(country => {
        return country.toLowerCase().startsWith(query)
      })
      if (startsWithMatch) return true
      
      return false
    })
    
    // Sort by relevance: exact matches first, then partial matches
    return filtered.sort((a, b) => {
      const queryLower = query.toLowerCase()
      
      // Check for exact matches in city names
      const aCity = getCityFromTimezone(a.timezone).toLowerCase()
      const bCity = getCityFromTimezone(b.timezone).toLowerCase()
      
      const aExactCity = aCity === queryLower
      const bExactCity = bCity === queryLower
      if (aExactCity && !bExactCity) return -1
      if (!aExactCity && bExactCity) return 1
      
      // Check for starts with in city names
      const aStartsCity = aCity.startsWith(queryLower)
      const bStartsCity = bCity.startsWith(queryLower)
      if (aStartsCity && !bStartsCity) return -1
      if (!aStartsCity && bStartsCity) return 1
      
      // Check for exact country matches
      const aExactCountry = a.countries.some(c => c.toLowerCase() === queryLower)
      const bExactCountry = b.countries.some(c => c.toLowerCase() === queryLower)
      if (aExactCountry && !bExactCountry) return -1
      if (!aExactCountry && bExactCountry) return 1
      
      // Default alphabetical
      return a.name.localeCompare(b.name)
    })
  }
  
  // Sort alphabetically by name when no search
  return filtered.sort((a, b) => a.name.localeCompare(b.name))
})

const selectedTimezone = computed(() => {
  return timezones.value.find(tz => tz.timezone === props.selectedTimezone) || timezones.value[0]
})

const getCurrentTimeForTimezone = (timezone) => {
  try {
    const now = new Date()
    let adjustedTime = now
    
    // If daylight savings is enabled, add 1 hour
    if (useDaylightSavings.value) {
      adjustedTime = new Date(now.getTime() + (60 * 60 * 1000))
    }
    
    return adjustedTime.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return '--:--'
  }
}

const getAdjustedOffset = (offset) => {
  if (!useDaylightSavings.value) {
    return offset
  }
  
  try {
    const isPositive = offset.includes('+')
    const hours = parseInt(offset.replace(/[+-]/, '').split(':')[0])
    const minutes = offset.includes(':') ? offset.split(':')[1] : '00'
    
    let adjustedHours = hours + 1
    
    // Handle edge cases
    if (adjustedHours > 14) adjustedHours = 14
    if (adjustedHours < -12) adjustedHours = -12
    
    const sign = isPositive ? '+' : '-'
    return `${sign}${adjustedHours.toString().padStart(2, '0')}:${minutes}`
  } catch {
    return offset
  }
}

// Watch for DST changes to update times
watch(useDaylightSavings, () => {
  // Force reactivity update
  currentTime.value = new Date()
})

const selectTimezone = (timezone) => {
  emit('update:selectedTimezone', timezone)
  showDropdown.value = false
  searchQuery.value = ''
}

const detectingTimezone = ref(false)
const detectedTimezone = ref(null)

const detectTimezone = () => {
  detectingTimezone.value = true
  detectedTimezone.value = null
  
  try {
    // Use browser's built-in timezone detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    if (timezone) {
      detectedTimezone.value = timezone
      
      // Check if detected timezone exists in our list
      const foundTimezone = timezones.value.find(tz => tz.timezone === timezone)
      
      if (foundTimezone) {
        // Small delay for visual feedback
        setTimeout(() => {
          emit('update:selectedTimezone', timezone)
          detectedTimezone.value = null
          detectingTimezone.value = false
        }, 500)
      } else {
        // Timezone detected but not in our list - still use it
        emit('update:selectedTimezone', timezone)
        detectedTimezone.value = null
        detectingTimezone.value = false
      }
    } else {
      throw new Error('Could not detect timezone')
    }
  } catch (error) {
    console.error('Failed to detect timezone:', error)
    alert('Unable to detect your timezone. Please select it manually.')
    detectingTimezone.value = false
    detectedTimezone.value = null
  }
}

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
  if (showDropdown.value) {
    searchQuery.value = ''
  }
}
</script>

<template>
  <div class="timezone-selector">
    <div class="mb-4">
      <h3 class="text-lg font-semibold text-gray-800 mb-2">Select Your Timezone</h3>
      <p class="text-sm text-gray-600">Choose from 200+ timezones covering all world countries or auto-detect your location</p>
    </div>

    <!-- Auto-detect Button -->
    <div class="mb-6">
      <button 
        @click="detectTimezone"
        :disabled="detectingTimezone"
        class="w-full px-6 py-4 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-xl hover:from-pink-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Auto-detect timezone using your browser settings"
      >
        <i v-if="detectingTimezone" class="fas fa-spinner fa-spin text-lg"></i>
        <i v-else class="fas fa-location-arrow text-lg"></i>
        <span class="font-medium text-lg">
          {{ detectingTimezone ? 'Detecting...' : 'Auto-Detect My Timezone' }}
        </span>
      </button>
      <p class="text-xs text-gray-500 mt-2 text-center">
        Uses your browser's location and system settings to detect your timezone
      </p>
      <div v-if="detectedTimezone" class="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-center gap-2 text-green-800 text-sm">
          <i class="fas fa-check-circle"></i>
          <span>Detected: {{ detectedTimezone }}</span>
        </div>
      </div>
    </div>

    <!-- Timezone Selector -->
    <div class="relative">
      <label class="block text-sm font-medium text-gray-700 mb-2">Or manually select your timezone</label>
      
      <!-- Selected Timezone Display -->
      <div 
        @click="toggleDropdown"
        class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white cursor-pointer hover:border-gray-400 transition-colors"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
            <div>
              <div class="font-medium text-gray-800">{{ selectedTimezone.name }}</div>
              <div class="text-sm text-gray-500">{{ selectedTimezone.countries.join(', ') }}</div>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <div class="text-right">
              <div class="text-sm font-mono text-gray-600">
                {{ getAdjustedOffset(selectedTimezone.offset) }}
              </div>
              <div class="text-sm font-mono text-gray-800">{{ getCurrentTimeForTimezone(selectedTimezone.timezone) }}</div>
            </div>
            <i class="fas fa-chevron-down text-gray-400 transition-transform" :class="{ 'rotate-180': showDropdown }"></i>
          </div>
        </div>
      </div>

      <!-- Dropdown -->
      <div v-if="showDropdown" class="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-[600px] overflow-hidden">
        <!-- Search Input -->
        <div class="p-3 border-b border-gray-200">
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Search timezones, countries (e.g. 'Estonia', 'New York', 'Tokyo')..."
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
            >
          </div>
          <div class="mt-2 text-xs text-gray-500">
            Showing {{ filteredTimezones.length }} of {{ timezones.length }} timezones
          </div>
        </div>

        <!-- Timezone List -->
        <div class="max-h-[500px] overflow-y-auto">
          <div 
            v-for="tz in filteredTimezones" 
            :key="tz.timezone"
            @click="selectTimezone(tz.timezone)"
            class="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
            :class="{ 'bg-pink-50 border-l-4 border-l-pink-500': tz.timezone === selectedTimezone.timezone }"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-3 h-3 rounded-full" :style="{ backgroundColor: tz.timezone === selectedTimezone.timezone ? '#EC4899' : '#10B981' }"></div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-800">
                    {{ tz.name }}
                    <span v-if="getCityFromTimezone(tz.timezone)" class="text-sm font-normal text-gray-600">
                      ({{ getCityFromTimezone(tz.timezone) }})
                    </span>
                  </div>
                  <div class="text-sm text-gray-500 truncate" :title="tz.countries.join(', ')">
                    {{ tz.countries.join(', ') }}
                  </div>
                  <div class="text-xs text-gray-400 mt-1 font-mono">{{ tz.timezone }}</div>
                </div>
              </div>
                             <div class="text-right">
                 <div class="text-sm font-mono text-gray-600">
                   {{ getAdjustedOffset(tz.offset) }}
                 </div>
                 <div class="text-sm font-mono text-gray-800">{{ getCurrentTimeForTimezone(tz.timezone) }}</div>
               </div>
            </div>
          </div>
        </div>

        <!-- No Results -->
        <div v-if="filteredTimezones.length === 0" class="p-4 text-center text-gray-500">
          <i class="fas fa-search text-2xl mb-2"></i>
          <p>No timezones found matching "{{ searchQuery }}"</p>
        </div>
      </div>
    </div>

    <!-- Daylight Savings Toggle -->
    <!-- <div class="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="font-medium text-gray-800">Daylight Saving Time</h4>
          <p class="text-sm text-gray-600">Toggle between standard time and daylight saving time</p>
        </div>
        <div class="relative inline-block w-12 h-6">
          <input 
            type="checkbox" 
            v-model="useDaylightSavings"
            class="sr-only peer"
          >
          <div 
            class="block bg-gray-200 rounded-full h-6 w-12 cursor-pointer 
                   peer-checked:bg-pink-500 transition-colors"
          ></div>
          <div 
            class="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform
                   peer-checked:translate-x-6"
          ></div>
        </div>
      </div>
      <div class="mt-2 text-xs text-gray-500">
        <span v-if="useDaylightSavings" class="text-pink-600 font-medium">DST Active</span>
        <span v-else class="text-gray-500">Standard Time</span>
      </div>
    </div> -->

    <!-- Current Selection Info -->
    <div v-if="selectedTimezone.timezone !== 'UTC'" class="mt-6 p-4 bg-pink-50 rounded-xl border border-pink-200">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="font-medium text-gray-800">Selected Timezone</h4>
          <p class="text-sm text-gray-600">{{ selectedTimezone.name }} ({{ selectedTimezone.countries.join(', ') }})</p>
          <p class="text-xs text-gray-500 mt-1">
            <span v-if="useDaylightSavings" class="text-pink-600">DST Time</span>
            <span v-else>Standard Time</span>
          </p>
        </div>
        <div class="text-right">
          <div class="text-lg font-mono text-gray-800">{{ getCurrentTimeForTimezone(selectedTimezone.timezone) }}</div>
          <div class="text-xs text-gray-500">
            {{ new Date().toLocaleDateString('en-US', { timeZone: selectedTimezone.timezone }) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.timezone-selector {
  position: relative;
}
</style>
